import React, { useMemo, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  TableSortLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Expand,
  Close,
  Download,
  PictureAsPdf,
  TableChart,
  FileDownload
} from '@mui/icons-material';
import BubbleMaps from './BuubleMapChart';

// Function to normalize company names
const normalizeCompanyName = companyName => {
  if (!companyName) return '';

  // Convert to lowercase and trim
  let normalized = companyName.toLowerCase().trim();

  // Remove special characters, punctuation
  normalized = normalized.replace(/[.,#@!$%^&*()_+={}[\]:;"'<>?/|`~-]/g, '');

  // Remove extra spaces
  normalized = normalized.replace(/\s+/g, ' ');

  // Common company suffixes to remove
  const suffixes = [
    'inc',
    'incorporated',
    'llc',
    'ltd',
    'limited',
    'corp',
    'corporation',
    'co',
    'company',
    'lp',
    'llp',
    'plc',
    'gmbh',
    'ag',
    'sarl',
    'sa',
    'coating',
    'coatings',
    'international',
    'industries',
    'group',
    'holdings',
    'technologies',
    'systems',
    'solutions',
    'services',
    'logistics',
    'transportation',
    'shipping',
    'distribution'
  ];

  // Remove suffixes
  suffixes.forEach(suffix => {
    const regex = new RegExp(`\\b${suffix}\\b`, 'gi');
    normalized = normalized.replace(regex, '');
  });

  // Remove words like "the", "and", "of"
  const articles = ['the', 'and', 'of', '&', 'for', 'to', 'in', 'on', 'at'];
  articles.forEach(article => {
    const regex = new RegExp(`\\b${article}\\b`, 'gi');
    normalized = normalized.replace(regex, '');
  });

  // Remove city/state names that might be part of company address
  const commonLocations = [
    // US states abbreviations and names
    'al',
    'ak',
    'az',
    'ar',
    'ca',
    'co',
    'ct',
    'de',
    'fl',
    'ga',
    'hi',
    'id',
    'il',
    'in',
    'ia',
    'ks',
    'ky',
    'la',
    'me',
    'md',
    'ma',
    'mi',
    'mn',
    'ms',
    'mo',
    'mt',
    'ne',
    'nv',
    'nh',
    'nj',
    'nm',
    'ny',
    'nc',
    'nd',
    'oh',
    'ok',
    'or',
    'pa',
    'ri',
    'sc',
    'sd',
    'tn',
    'tx',
    'ut',
    'vt',
    'va',
    'wa',
    'wv',
    'wi',
    'wy',
    'alabama',
    'alaska',
    'arizona',
    'arkansas',
    'california',
    'colorado',
    'connecticut',
    'delaware',
    'florida',
    'georgia',
    'hawaii',
    'idaho',
    'illinois',
    'indiana',
    'iowa',
    'kansas',
    'kentucky',
    'louisiana',
    'maine',
    'maryland',
    'massachusetts',
    'michigan',
    'minnesota',
    'mississippi',
    'missouri',
    'montana',
    'nebraska',
    'nevada',
    'hampshire',
    'jersey',
    'mexico',
    'york',
    'carolina',
    'dakota',
    'ohio',
    'oklahoma',
    'oregon',
    'pennsylvania',
    'rhode',
    'carolina',
    'dakota',
    'tennessee',
    'texas',
    'utah',
    'vermont',
    'virginia',
    'washington',
    'west virginia',
    'wisconsin',
    'wyoming',
    // Common city names
    'new york',
    'los angeles',
    'chicago',
    'houston',
    'phoenix',
    'philadelphia',
    'san antonio',
    'san diego',
    'dallas',
    'san jose'
  ];

  commonLocations.forEach(location => {
    const regex = new RegExp(`\\b${location}\\b`, 'gi');
    normalized = normalized.replace(regex, '');
  });

  // Remove numbers
  normalized = normalized.replace(/\d+/g, '');

  // Remove standalone single letters
  normalized = normalized.replace(/\b\w\b/g, '');

  // Clean up: remove extra spaces, trim
  normalized = normalized.replace(/\s+/g, ' ').trim();

  // Specific company normalizations
  if (normalized.includes('ifs')) {
    return 'ifs';
  }

  if (normalized.includes('aalchem') || normalized.includes('aal chame')) {
    return 'aalchem';
  }

  // Extract first meaningful word or acronym
  const words = normalized.split(' ').filter(word => word.length > 1);

  if (words.length === 0) {
    return companyName.toLowerCase(); // Fallback to original
  }

  // Check if it's likely an acronym (all caps or short)
  const originalWords = companyName.split(' ');
  if (originalWords.length === 1 && originalWords[0].length <= 5) {
    return originalWords[0].toLowerCase();
  }

  // For multi-word names, try to find the core name
  // Remove common prefixes
  const prefixes = [
    'advanced',
    'american',
    'global',
    'national',
    'international',
    'united'
  ];
  const filteredWords = words.filter(word => !prefixes.includes(word));

  return filteredWords.length > 0 ? filteredWords[0] : words[0];
};

// Updated processSummary function with better grouping logic
const processSummary = (shipments, type = 'origin') => {
  const stats = {};
  const companyDetails = {};

  shipments.forEach(shipment => {
    // Get the raw company name based on type
    const rawCompanyName =
      type === 'origin' ? shipment.originCompany : shipment.destinationCompany;

    const state =
      type === 'origin' ? shipment.originState : shipment.destinationState;

    if (!rawCompanyName || !state) return;

    // Normalize the company name
    const normalizedName = normalizeCompanyName(rawCompanyName);

    // Create a combined key: normalizedName + state
    // This groups same company in same state together
    const key = `${normalizedName}-${state}`;

    // Track company variations for this normalized name
    if (!companyDetails[normalizedName]) {
      companyDetails[normalizedName] = {
        originalNames: new Set(),
        states: new Set(),
        mostCommonName: rawCompanyName,
        count: 0
      };
    }

    companyDetails[normalizedName].originalNames.add(rawCompanyName);
    companyDetails[normalizedName].states.add(state);
    companyDetails[normalizedName].count++;

    if (!stats[key]) {
      stats[key] = {
        shipments: 0,
        freightSpend: 0,
        totalWeight: 0,
        normalizedName: normalizedName,
        rawCompanyName: rawCompanyName,
        originalNames: new Set([rawCompanyName]),
        state: state,
        allStates: new Set([state])
      };
    } else {
      stats[key].originalNames.add(rawCompanyName);
      stats[key].allStates.add(state);
    }

    stats[key].shipments += 1;
    stats[key].freightSpend += shipment.quoteNetCharge;
    stats[key].totalWeight += shipment.totalNetWeight || 0;
  });

  // Determine display name for each normalized company
  const displayNameMap = {};
  Object.entries(companyDetails).forEach(([normalizedName, data]) => {
    const namesArray = Array.from(data.originalNames);

    // Find the most appropriate display name
    // 1. Try to find the shortest clean name
    const cleanNames = namesArray.filter(name => {
      const clean = name.toLowerCase().replace(/[^a-z0-9\s]/g, '');
      return clean.split(' ').some(word => word.length > 2);
    });

    if (cleanNames.length > 0) {
      const shortestCleanName = cleanNames.reduce(
        (shortest, current) =>
          current.length < shortest.length ? current : shortest,
        cleanNames[0]
      );
      displayNameMap[normalizedName] = shortestCleanName.toUpperCase();
    } else {
      // 2. Use the most common name
      displayNameMap[normalizedName] = data.mostCommonName.toUpperCase();
    }
  });

  const result = Object.entries(stats).map(([key, data]) => {
    const avgShipmentCost =
      data.shipments > 0 ? data.freightSpend / data.shipments : 0;
    const avgShipmentWeight =
      data.shipments > 0 ? data.totalWeight / data.shipments : 0;
    const costPerPound =
      data.totalWeight > 0 ? data.freightSpend / data.totalWeight : 0;

    const displayName =
      displayNameMap[data.normalizedName] || data.normalizedName.toUpperCase();
    const originalNames = Array.from(data.originalNames);
    const hasMultipleVariations = data.originalNames.size > 1;
    const operatesInMultipleStates = data.allStates.size > 1;

    // Create name for display
    let displayNameWithState = `${displayName} - ${data.state}`;

    // Add indicator if company operates in multiple states
    if (operatesInMultipleStates) {
      const otherStates = Array.from(data.allStates).filter(
        s => s !== data.state
      );
      if (otherStates.length > 0) {
        displayNameWithState += ` (also in ${otherStates.join(', ')})`;
      }
    }

    return {
      key: key,
      name: displayNameWithState,
      displayName: displayName,
      normalizedName: data.normalizedName,
      state: data.state,
      allStates: Array.from(data.allStates),
      originalNames: originalNames,
      hasMultipleVariations: hasMultipleVariations,
      operatesInMultipleStates: operatesInMultipleStates,
      shipments: data.shipments,
      freightSpend: data.freightSpend,
      costPerPound: costPerPound,
      avgShipmentCost: avgShipmentCost,
      avgShipmentWeight: avgShipmentWeight,
      volumeShipped: data.totalWeight
    };
  });

  return result;
};

// Helper function to add to the normalizeCompanyName for specific patterns
const enhanceNormalizeCompanyName = () => {
  // You can add more specific company patterns here
  const companyPatterns = [
    {
      pattern: /(aalchem|aal\s*chame|alchem)/i,
      normalized: 'aalchem'
    },
    {
      pattern: /^ifs\b|ifs\s+(coatings|coating|inc|incorporated|llc|ltd|corp)/i,
      normalized: 'ifs'
    },
    {
      pattern: /^acme\b|acme\s+(inc|incorporated|llc|ltd|corp)/i,
      normalized: 'acme'
    }
    // Add more patterns as needed
  ];

  return companyName => {
    if (!companyName) return '';

    let normalized = companyName.toLowerCase().trim();

    // Apply specific patterns first
    for (const pattern of companyPatterns) {
      if (pattern.pattern.test(normalized)) {
        return pattern.normalized;
      }
    }

    // Then apply general normalization
    return normalizeCompanyName(normalized);
  };
};

// // Data Processing Functions - Updated with company name normalization
// const processSummary = (shipments, type = 'origin') => {
//   const stats = {};
//   const originalNameMap = {};

//   shipments.forEach(shipment => {
//     // Get the raw company name based on type
//     const rawCompanyName =
//       type === 'origin' ? shipment.originCompany : shipment.destinationCompany;

//     const state =
//       type === 'origin' ? shipment.originState : shipment.destinationState;

//     if (!rawCompanyName) return;

//     // Normalize the company name
//     const normalizedName = normalizeCompanyName(rawCompanyName);

//     // Create a combined key with normalized name and state
//     const key = `${normalizedName} - ${state}`;

//     // Track original names for this normalized name
//     if (!originalNameMap[normalizedName]) {
//       originalNameMap[normalizedName] = {
//         names: new Set(),
//         mostCommonName: rawCompanyName,
//         count: 0
//       };
//     }

//     originalNameMap[normalizedName].names.add(rawCompanyName);
//     originalNameMap[normalizedName].count++;

//     if (!stats[key]) {
//       stats[key] = {
//         shipments: 0,
//         freightSpend: 0,
//         totalWeight: 0,
//         normalizedName: normalizedName,
//         rawCompanyName: rawCompanyName,
//         originalNames: new Set([rawCompanyName]),
//         state: state
//       };
//     } else {
//       stats[key].originalNames.add(rawCompanyName);
//     }

//     stats[key].shipments += 1;
//     stats[key].freightSpend += shipment.quoteNetCharge;
//     stats[key].totalWeight += shipment.totalNetWeight || 0;
//   });

//   // Determine display names (use the shortest original name)
//   const displayNameMap = {};
//   Object.entries(originalNameMap).forEach(([normalizedName, data]) => {
//     const namesArray = Array.from(data.names);
//     const shortestName = namesArray.reduce(
//       (shortest, current) =>
//         current.length < shortest.length ? current : shortest,
//       namesArray[0]
//     );
//     displayNameMap[normalizedName] = shortestName;
//   });

//   const result = Object.entries(stats).map(([key, stats]) => {
//     const avgShipmentCost =
//       stats.shipments > 0 ? stats.freightSpend / stats.shipments : 0;
//     const avgShipmentWeight =
//       stats.shipments > 0 ? stats.totalWeight / stats.shipments : 0;
//     const costPerPound =
//       stats.totalWeight > 0 ? stats.freightSpend / stats.totalWeight : 0;

//     const displayName =
//       displayNameMap[stats.normalizedName] || stats.rawCompanyName;
//     const hasMultipleVariations = stats.originalNames.size > 1;
//     const originalNames = Array.from(stats.originalNames);

//     return {
//       name: `${displayName} - ${stats.state}`,
//       displayName,
//       state: stats.state,
//       normalizedName: stats.normalizedName,
//       originalNames,
//       hasMultipleVariations,
//       shipments: stats.shipments,
//       freightSpend: stats.freightSpend,
//       costPerPound,
//       avgShipmentCost,
//       avgShipmentWeight,
//       volumeShipped: stats.totalWeight
//     };
//   });

//   return result;
// };

const processTransposedStateLaneData = (shipments, type = 'origin') => {
  const laneStats = {};
  shipments.forEach(shipment => {
    const originState = shipment.originState || 'Unknown';
    const destinationState = shipment.destinationState || 'Unknown';
    if (originState === 'Unknown' || destinationState === 'Unknown') return;

    // For origin tab: focus on origin states
    // For destination tab: focus on destination states
    let laneKey;
    if (type === 'origin') {
      // For origin tab: show lanes FROM this origin
      laneKey = `${originState} - ${destinationState}`;
    } else {
      // For destination tab: show lanes TO this destination
      laneKey = `${originState} - ${destinationState}`;
    }

    if (!laneStats[laneKey]) {
      laneStats[laneKey] = {
        shipments: 0,
        freightSpend: 0,
        totalWeight: 0,
        costHistory: []
      };
    }
    laneStats[laneKey].shipments += 1;
    laneStats[laneKey].freightSpend += shipment.quoteNetCharge;
    laneStats[laneKey].totalWeight += shipment.totalNetWeight || 0;
  });

  const lanes = Object.entries(laneStats)
    .map(([lane, stats]) => {
      const avgShipmentCost =
        stats.shipments > 0 ? stats.freightSpend / stats.shipments : 0;
      const avgShipmentWeight =
        stats.shipments > 0 ? stats.totalWeight / stats.shipments : 0;
      const costPerPound =
        stats.totalWeight > 0 ? stats.freightSpend / stats.totalWeight : 0;

      return {
        lane,
        avgShipmentCost,
        avgShipmentWeight,
        costPerPound,
        freightSpend: stats.freightSpend,
        shipments: stats.shipments,
        weight: stats.totalWeight
      };
    })
    .sort((a, b) => b.shipments - a.shipments);

  const metrics = [
    { name: 'Avg. Shipment Cost', key: 'avgShipmentCost', format: 'currency' },
    {
      name: 'Avg. Shipment Weight (lbs)',
      key: 'avgShipmentWeight',
      format: 'weight'
    },
    { name: 'Cost per Pound', key: 'costPerPound', format: 'currencyPerPound' },
    { name: 'Freight Spend', key: 'freightSpend', format: 'currency' },
    { name: 'Shipments', key: 'shipments', format: 'number' },
    { name: 'Weight (lbs)', key: 'weight', format: 'weight' }
  ];

  return {
    metrics,
    lanes,
    laneData: lanes.reduce((acc, lane) => {
      acc[lane.lane] = lane;
      return acc;
    }, {})
  };
};

// Export Functions (same as before)
const exportToExcel = (data, tableName) => {
  try {
    if (window.XLSX) {
      const ws = window.XLSX.utils.json_to_sheet(data);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, tableName);
      window.XLSX.writeFile(
        wb,
        `${tableName}_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    } else {
      exportToCSV(data, tableName);
    }
  } catch (error) {
    console.error('Excel export error:', error);
    exportToCSV(data, tableName);
  }
};

const exportToCSV = (data, tableName) => {
  try {
    if (window.Papa) {
      const csv = window.Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `${tableName}_${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(row =>
        Object.values(row)
          .map(value =>
            typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
          )
          .join(',')
      );
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      window.saveAs(
        blob,
        `${tableName}_${new Date().toISOString().split('T')[0]}.csv`
      );
    }
  } catch (error) {
    console.error('CSV export error:', error);
    alert('Error exporting data. Please try again.');
  }
};

const exportToPDF = (tableType, summaryData, transposedData, isOrigin) => {
  try {
    if (window.html2pdf) {
      let element;
      const title = isOrigin ? 'Origin' : 'Destination';

      if (tableType === 'summary') {
        const tempDiv = document.createElement('div');
        tempDiv.id = 'pdf-export-summary';
        tempDiv.style.padding = '20px';

        let html = `
          <h2>${title} Summary</h2>
          <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th>${isOrigin ? 'Shipper' : 'Consignee'} Name</th>
                <th>Shipments</th>
                <th>Freight Spend</th>
                <th>Cost per Pound</th>
                <th>Avg. Shipment Cost</th>
                <th>Avg. Shipment Weight (lbs)</th>
                <th>Volume Shipped</th>
              </tr>
            </thead>
            <tbody>
        `;

        summaryData.forEach(row => {
          html += `
            <tr>
              <td>${row.name || ''}</td>
              <td align="center">${row.shipments.toLocaleString()}</td>
              <td align="right">$${row.freightSpend.toLocaleString()}</td>
              <td align="right">$${row.costPerPound.toFixed(2)}</td>
              <td align="right">$${row.avgShipmentCost.toLocaleString()}</td>
              <td align="right">${row.avgShipmentWeight.toLocaleString()} lbs</td>
              <td align="right">${row.volumeShipped.toLocaleString()} lbs</td>
            </tr>
          `;
        });

        html += `
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 10px; color: #666;">
            Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
          </p>
        `;

        tempDiv.innerHTML = html;
        document.body.appendChild(tempDiv);
        element = tempDiv;
      } else {
        const tempDiv = document.createElement('div');
        tempDiv.id = 'pdf-export-state-lane';
        tempDiv.style.padding = '20px';

        let html = `
          <h2>${title} Lane Analysis</h2>
          <table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 10px;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th>Metric</th>
        `;

        transposedData.lanes.forEach(lane => {
          html += `<th align="right">${lane.lane}</th>`;
        });

        html += `</tr></thead><tbody>`;

        transposedData.metrics.forEach(metric => {
          html += `<tr><td><strong>${metric.name}</strong></td>`;

          transposedData.lanes.forEach(lane => {
            const value = transposedData.laneData[lane.lane][metric.key];
            let formattedValue;

            switch (metric.format) {
              case 'currency':
                formattedValue = `$${value.toLocaleString()}`;
                break;
              case 'currencyPerPound':
                formattedValue = `$${value.toFixed(2)}`;
                break;
              case 'weight':
                formattedValue = `${value.toLocaleString()} lbs`;
                break;
              case 'number':
                formattedValue = value.toLocaleString();
                break;
              default:
                formattedValue = value;
            }

            html += `<td align="right">${formattedValue}</td>`;
          });

          html += `</tr>`;
        });

        html += `
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 10px; color: #666;">
            Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
          </p>
        `;

        tempDiv.innerHTML = html;
        document.body.appendChild(tempDiv);
        element = tempDiv;
      }

      const opt = {
        margin: 0.5,
        filename: `${
          tableType === 'summary'
            ? `${title}_Summary`
            : `${title}_Lane_Analysis`
        }_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: {
          unit: 'in',
          format: tableType === 'summary' ? 'letter' : 'landscape',
          orientation: tableType === 'summary' ? 'portrait' : 'landscape'
        }
      };

      window
        .html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
    } else {
      window.print();
    }
  } catch (error) {
    console.error('PDF export error:', error);
    alert('PDF export failed. Please try exporting as Excel or CSV instead.');
  }
};

// Main Component
const OriginDestinationTab = ({ shipments = [], tabValue }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [expandedChart, setExpandedChart] = useState(null);
  const [expandedTable, setExpandedTable] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [selectedTableForExport, setSelectedTableForExport] = useState(null);
  const [orderBy, setOrderBy] = useState('shipments');
  const [order, setOrder] = useState('desc');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Determine if this is Origin (tabValue = 2) or Destination (tabValue = 3)
  const isOrigin = tabValue === 2;
  const tabType = isOrigin ? 'origin' : 'destination';
  const tabTitle = isOrigin ? 'Origin' : 'Destination';

  // Process data based on tab type
  const summaryDataRaw = useMemo(
    () => processSummary(shipments, tabType),
    [shipments, tabType]
  );

  const transposedStateLaneData = useMemo(
    () => processTransposedStateLaneData(shipments, tabType),
    [shipments, tabType]
  );

  // Sort summary data
  const summaryData = useMemo(() => {
    const sorted = [...summaryDataRaw];
    sorted.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'name') {
        aValue = aValue || '';
        bValue = bValue || '';
        return order === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      if (aValue < bValue) return order === 'desc' ? 1 : -1;
      if (aValue > bValue) return order === 'desc' ? -1 : 1;
      return 0;
    });
    return sorted;
  }, [summaryDataRaw, orderBy, order]);

  const handleSortRequest = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate totals
  const calculateTotals = data => {
    if (data.length === 0)
      return {
        shipments: 0,
        freightSpend: 0,
        costPerPound: 0,
        avgShipmentCost: 0,
        avgShipmentWeight: 0,
        volumeShipped: 0
      };

    return data.reduce(
      (acc, row) => ({
        shipments: acc.shipments + row.shipments,
        freightSpend: acc.freightSpend + row.freightSpend,
        costPerPound:
          (acc.costPerPound * acc.volumeShipped +
            row.costPerPound * row.volumeShipped) /
          (acc.volumeShipped + row.volumeShipped || 1),
        avgShipmentCost:
          (acc.avgShipmentCost * acc.shipments +
            row.avgShipmentCost * row.shipments) /
          (acc.shipments + row.shipments || 1),
        avgShipmentWeight:
          (acc.avgShipmentWeight * acc.shipments +
            row.avgShipmentWeight * row.shipments) /
          (acc.shipments + row.shipments || 1),
        volumeShipped: acc.volumeShipped + row.volumeShipped
      }),
      {
        shipments: 0,
        freightSpend: 0,
        costPerPound: 0,
        avgShipmentCost: 0,
        avgShipmentWeight: 0,
        volumeShipped: 0
      }
    );
  };

  const totals = calculateTotals(summaryData);

  // Export handlers
  const handleExportClick = (event, tableType) => {
    setExportAnchorEl(event.currentTarget);
    setSelectedTableForExport(tableType);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
    setSelectedTableForExport(null);
  };

  const handleExport = format => {
    if (selectedTableForExport === 'summary') {
      const exportData = summaryData.map(row => ({
        [`${tabTitle} Display Name`]: row.displayName,
        [`${tabTitle} State`]: row.state,
        [`${tabTitle} All States`]: row.allStates.join('; '),
        [`${tabTitle} Original Name Variations`]: row.originalNames.join('; '),
        [`${tabTitle} Variation Count`]: row.originalNames.length,
        Shipments: row.shipments,
        'Freight Spend': row.freightSpend,
        'Cost per Pound': row.costPerPound,
        'Avg. Shipment Cost': row.avgShipmentCost,
        'Avg. Shipment Weight (lbs)': row.avgShipmentWeight,
        'Volume Shipped (lbs)': row.volumeShipped
      }));

      if (format === 'excel') {
        exportToExcel(exportData, `${tabTitle}_Summary`);
      } else if (format === 'pdf') {
        exportToPDF('summary', summaryData, transposedStateLaneData, isOrigin);
      } else if (format === 'csv') {
        exportToCSV(exportData, `${tabTitle}_Summary`);
      }
    } else if (selectedTableForExport === 'stateLane') {
      const exportData = transposedStateLaneData.metrics.map(metric => {
        const row = { Metric: metric.name };
        transposedStateLaneData.lanes.forEach(lane => {
          const value = transposedStateLaneData.laneData[lane.lane][metric.key];
          row[lane.lane] = value;
        });
        return row;
      });

      if (format === 'excel') {
        exportToExcel(exportData, `${tabTitle}_Lane_Analysis`);
      } else if (format === 'pdf') {
        exportToPDF(
          'stateLane',
          summaryData,
          transposedStateLaneData,
          isOrigin
        );
      } else if (format === 'csv') {
        exportToCSV(exportData, `${tabTitle}_Lane_Analysis`);
      }
    }

    handleExportClose();
  };

  // Toggle expanded view
  const toggleExpandedTable = tableType => {
    setExpandedTable(expandedTable === tableType ? null : tableType);
  };

  // Formatting functions
  const formatCurrency = value =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  const formatNumber = value =>
    new Intl.NumberFormat('en-US').format(Math.round(value));
  const formatWeight = value => `${formatNumber(value)} lbs`;
  const formatCostPerPound = value => `$${value.toFixed(3)}`;

  if (shipments.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant='body2' color='textSecondary'>
          Loading shipment data...
        </Typography>
      </Box>
    );
  }

  // Render expanded table dialog
  const renderExpandedTable = () => {
    if (!expandedTable) return null;

    const isSummary = expandedTable === 'summary';
    const title = isSummary
      ? `${tabTitle} Summary - Full View`
      : `${tabTitle} Lane Analysis - Full View`;

    return (
      <Dialog
        open={true}
        onClose={() => setExpandedTable(null)}
        maxWidth='xl'
        fullWidth
        PaperProps={{
          sx: { height: '95vh' }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#1e40af',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1
          }}
        >
          <Typography variant='h6' sx={{ fontSize: '1rem' }}>
            {title}
          </Typography>
          <Box>
            <Tooltip title='Export'>
              <IconButton
                onClick={e => handleExportClick(e, expandedTable)}
                sx={{ color: 'white', mr: 1 }}
                size='small'
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close'>
              <IconButton
                onClick={() => setExpandedTable(null)}
                sx={{ color: 'white' }}
                size='small'
              >
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {isSummary ? (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        minWidth: 200
                      }}
                    >
                      {isOrigin ? 'Shipper' : 'Consignee'} Name
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Shipments
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Freight Spend
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Cost per Pound
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Avg. Shipment Cost
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Avg. Shipment Weight (lbs)
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      Volume Shipped
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryData.map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Tooltip
                          title={
                            <Box>
                              <div>
                                <strong>Company Variations:</strong>
                              </div>
                              {row.originalNames.map((name, idx) => (
                                <div key={idx}>• {name}</div>
                              ))}
                              {row.operatesInMultipleStates && (
                                <div style={{ marginTop: '4px' }}>
                                  <strong>Operates in states:</strong>{' '}
                                  {row.allStates.join(', ')}
                                </div>
                              )}
                            </Box>
                          }
                          placement='top-start'
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {row.displayName}
                            {row.hasMultipleVariations && (
                              <Chip
                                label={`${row.originalNames.length} variations`}
                                size='small'
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  backgroundColor: 'rgba(0, 0, 0, 0.08)'
                                }}
                              />
                            )}
                            {row.operatesInMultipleStates && (
                              <Chip
                                label={`${row.allStates.length} states`}
                                size='small'
                                sx={{
                                  height: '18px',
                                  fontSize: '0.65rem',
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  color: '#1976d2'
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align='center'>
                        {formatNumber(row.shipments)}
                      </TableCell>
                      <TableCell align='center'>
                        {formatCurrency(row.freightSpend.toFixed(0))}
                      </TableCell>
                      <TableCell align='center'>
                        {formatCostPerPound(row.costPerPound)}
                      </TableCell>
                      <TableCell align='center'>
                        {formatCurrency(row.avgShipmentCost.toFixed(0))}
                      </TableCell>
                      <TableCell align='center'>
                        {formatWeight(row.avgShipmentWeight)}
                      </TableCell>
                      <TableCell align='center'>
                        {formatWeight(row.volumeShipped)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        minWidth: 200
                      }}
                    >
                      {/* Empty header */}
                    </TableCell>
                    {transposedStateLaneData.lanes.map((lane, index) => (
                      <TableCell
                        key={index}
                        align='right'
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          minWidth: 120
                        }}
                      >
                        {lane.lane}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transposedStateLaneData.metrics.map(
                    (metric, metricIndex) => (
                      <TableRow key={metricIndex}>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor:
                              metricIndex % 2 === 0 ? '#fafafa' : 'white',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            minWidth: 200
                          }}
                        >
                          {metric.name}
                        </TableCell>
                        {transposedStateLaneData.lanes.map(
                          (lane, laneIndex) => {
                            const value =
                              transposedStateLaneData.laneData[lane.lane][
                                metric.key
                              ];
                            let formattedValue;
                            switch (metric.format) {
                              case 'currency':
                                formattedValue = formatCurrency(
                                  Math.round(value)
                                );
                                break;
                              case 'currencyPerPound':
                                formattedValue = formatCostPerPound(value);
                                break;
                              case 'weight':
                                formattedValue = formatWeight(value);
                                break;
                              case 'number':
                                formattedValue = formatNumber(value);
                                break;
                              default:
                                formattedValue = value;
                            }
                            return (
                              <TableCell
                                key={laneIndex}
                                align='right'
                                sx={{
                                  backgroundColor:
                                    metricIndex % 2 === 0 ? '#fafafa' : 'white'
                                }}
                              >
                                {formattedValue}
                              </TableCell>
                            );
                          }
                        )}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => handleExport('excel')}>
          <TableChart sx={{ mr: 1, fontSize: 20 }} />
          Export to Excel
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <FileDownload sx={{ mr: 1, fontSize: 20 }} />
          Export to CSV
        </MenuItem>
      </Menu>

      {/* Expanded Table Dialog */}
      {renderExpandedTable()}

      {/* Row 1: Bubble Map + Summary */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 1,
          mb: 1
        }}
      >
        {/* Bubble Map */}
        <Paper
          sx={{
            flex: 0.5,
            minHeight: 'calc(100vh - 435px)'
          }}
        >
          <Box sx={{ height: 'calc(100vh - 435px)' }}>
            <BubbleMaps
              shipments={shipments}
              setExpandedChart={setExpandedChart}
              expandedChart={expandedChart}
              tabValue={tabValue}
            />
          </Box>
        </Paper>

        {/* Summary Table */}
        <Paper
          sx={{
            flex: 1,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 435px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#1e40af',
              p: 0.5,
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 'bold',
                color: 'white',
                fontSize: '0.8rem',
                textAlign: 'center',
                flex: 1
              }}
            >
              {tabTitle} Summary
            </Typography>
            <Box>
              <Tooltip title='Export'>
                <IconButton
                  onClick={e => handleExportClick(e, 'summary')}
                  sx={{ color: 'white', p: 0.25 }}
                  size='small'
                >
                  <Download fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Expand view'>
                <IconButton
                  onClick={() => toggleExpandedTable('summary')}
                  sx={{ color: 'white', p: 0.25 }}
                  size='small'
                >
                  <Expand fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto', flex: 1 }}
          >
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow sx={{ whiteSpace: 'nowrap' }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5,
                      borderRight: '1px solid #ddd'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSortRequest('name')}
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        minWidth: 200
                      }}
                    >
                      {isOrigin ? 'Shipper' : 'Consignee'} Name
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'shipments'}
                      direction={orderBy === 'shipments' ? order : 'desc'}
                      onClick={() => handleSortRequest('shipments')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Shipments
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'freightSpend'}
                      direction={orderBy === 'freightSpend' ? order : 'desc'}
                      onClick={() => handleSortRequest('freightSpend')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Freight Spend
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'costPerPound'}
                      direction={orderBy === 'costPerPound' ? order : 'desc'}
                      onClick={() => handleSortRequest('costPerPound')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Cost per Pound
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'avgShipmentCost'}
                      direction={orderBy === 'avgShipmentCost' ? order : 'desc'}
                      onClick={() => handleSortRequest('avgShipmentCost')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Avg. Shipment Cost
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'avgShipmentWeight'}
                      direction={
                        orderBy === 'avgShipmentWeight' ? order : 'desc'
                      }
                      onClick={() => handleSortRequest('avgShipmentWeight')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Avg. Shipment Weight (lbs)
                    </TableSortLabel>
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'volumeShipped'}
                      direction={orderBy === 'volumeShipped' ? order : 'desc'}
                      onClick={() => handleSortRequest('volumeShipped')}
                      sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
                    >
                      Volume Shipped
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.map((row, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell
                      sx={{
                        fontSize: '0.8rem',
                        py: 0.5,
                        maxWidth: 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        borderRight: '1px solid #ddd'
                      }}
                    >
                      <Tooltip
                        title={
                          row.hasMultipleVariations ? (
                            <Box>
                              <div>
                                <strong>Consolidated from:</strong>
                              </div>
                              {row.originalNames.map((name, idx) => (
                                <div key={idx}>• {name}</div>
                              ))}
                            </Box>
                          ) : (
                            row.name
                          )
                        }
                        placement='top-start'
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          {row.name.toUpperCase()}
                          {row.hasMultipleVariations && (
                            <Chip
                              label={`${row.originalNames.length} variations`}
                              size='small'
                              sx={{
                                height: '18px',
                                fontSize: '0.65rem',
                                backgroundColor: 'rgba(0, 0, 0, 0.08)'
                              }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatNumber(row.shipments)}
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatCurrency(row.freightSpend.toFixed(0))}
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatCostPerPound(row.costPerPound)}
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatCurrency(row.avgShipmentCost.toFixed(0))}
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatWeight(row.avgShipmentWeight)}
                    </TableCell>

                    <TableCell
                      align='center'
                      sx={{ fontSize: '0.8rem', py: 0.5 }}
                    >
                      {formatWeight(row.volumeShipped)}
                    </TableCell>
                  </TableRow>
                ))}

                {summaryData.length > 0 && (
                  <TableRow
                    sx={{
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      bottom: 0,
                      '&:hover': { backgroundColor: '#eeeeee' }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd',
                        borderRight: '1px solid #ddd'
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatNumber(totals.shipments)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatCurrency(totals.freightSpend)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatCostPerPound(totals.costPerPound)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatCurrency(totals.avgShipmentCost.toFixed(0))}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatWeight(totals.avgShipmentWeight)}
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        py: 0.5,
                        borderTop: '1px solid #ddd'
                      }}
                    >
                      {formatWeight(totals.volumeShipped)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      {/* Row 2: State Lane Table */}
      <Paper
        sx={{
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          mt: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            backgroundColor: '#1e40af',
            p: 0.5,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 'bold',
              color: 'white',
              fontSize: '0.8rem',
              textAlign: 'center',
              flex: 1
            }}
          >
            {tabTitle} Lane Table
          </Typography>
          <Box>
            <Tooltip title='Export'>
              <IconButton
                onClick={e => handleExportClick(e, 'stateLane')}
                sx={{ color: 'white', p: 0.25 }}
                size='small'
              >
                <Download fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Expand view'>
              <IconButton
                onClick={() => toggleExpandedTable('stateLane')}
                sx={{ color: 'white', p: 0.25 }}
                size='small'
              >
                <Expand fontSize='small' />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ width: '100%', overflow: 'auto', flex: 1 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    fontSize: '0.8rem',
                    py: 0.5,
                    minWidth: 190,
                    borderRight: '1px solid #ddd',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2
                  }}
                >
                  {/* Empty header */}
                </TableCell>
                {transposedStateLaneData.lanes.map((lane, index) => (
                  <TableCell
                    key={index}
                    align='right'
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      fontSize: '0.8rem',
                      py: 0.5,
                      minWidth: 110
                    }}
                  >
                    {lane.lane}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transposedStateLaneData.metrics.map((metric, metricIndex) => (
                <TableRow key={metricIndex}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      py: 0.5,
                      backgroundColor:
                        metricIndex % 2 === 0 ? '#fafafa' : 'white',
                      borderRight: '1px solid #ddd',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1
                    }}
                  >
                    {metric.name}
                  </TableCell>
                  {transposedStateLaneData.lanes.map((lane, laneIndex) => {
                    const value =
                      transposedStateLaneData.laneData[lane.lane][metric.key];
                    let formattedValue;
                    switch (metric.format) {
                      case 'currency':
                        formattedValue = formatCurrency(Math.round(value));
                        break;
                      case 'currencyPerPound':
                        formattedValue = formatCostPerPound(value);
                        break;
                      case 'weight':
                        formattedValue = formatWeight(value);
                        break;
                      case 'number':
                        formattedValue = formatNumber(value);
                        break;
                      default:
                        formattedValue = value;
                    }
                    return (
                      <TableCell
                        key={laneIndex}
                        align='right'
                        sx={{
                          fontSize: '0.8rem',
                          py: 0.5,
                          backgroundColor:
                            metricIndex % 2 === 0 ? '#fafafa' : 'white'
                        }}
                      >
                        {formattedValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

export default OriginDestinationTab;
